import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import React, { useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { UserContext } from "../../Context/UserContext";
import { Error } from "../../Utils/Errors";
import { saveToLocal } from "../../Utils/LocalStorage";
import { useApi } from "../../Utils/useApi";

interface SignalRConnectionInfo {
  url: string;
  accessToken: string;
}

const signalRReconnectTimeout = process.env.REACT_APP_SIGNALR_RECONNECTION_TIMEOUT_SECONDS
  ? Number(process.env.REACT_APP_SIGNALR_RECONNECTION_TIMEOUT_SECONDS)
  : 3;

const SignalRConnection: React.FC = () => {
  const {
    ivrState,
    signalR: {
      payloads: { userPoll },
      srConnection,
      srConnectionInfo,
      setPayloads,
      setTopics,
      setSrConnection,
      setSrConnectionInfo,
      subscribe
    },
    token
  } = useContext(UserContext);

  const ivrStateRef = useRef(ivrState);
  useEffect(() => {
    ivrStateRef.current = ivrState;
  }, [ivrState]);

  // INITIALIZE THE SIGNAL R CONNECTION AND MANAGE ITS BEHAVIORS
  const { response: connInfoResponse } = useApi<SignalRConnectionInfo>({
    method: "post",
    url: "Legacy/V0/SignalR/Negotiate",
    config: { headers: { Authorization: `Bearer ${token?.token}` } },
    valid: !srConnectionInfo && !srConnection && !!token?.token,
    error: Error.PostSignalRNegotiate
  });
  // ON SR CONNECTION INFO QUERY SUCCESS
  useEffect(() => {
    if (connInfoResponse) {
      setSrConnectionInfo(connInfoResponse);
      saveToLocal(connInfoResponse, "signalRConnectionInfo");
    }
  }, [connInfoResponse, setSrConnectionInfo]);

  // ON SR CONNECTION INFO HAS VALUE
  useEffect(() => {
    if (srConnectionInfo && token && !srConnection) {
      // configure the connection
      const localSrConnection = new HubConnectionBuilder()
        .withUrl(srConnectionInfo.url, {
          accessTokenFactory: () => `${srConnectionInfo.accessToken}`
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => signalRReconnectTimeout * 1000
        })
        .configureLogging(LogLevel.Warning)
        .build();

      // set connection event bahviors
      localSrConnection.onclose(() => {
        localSrConnection.stop();
        if (toast.isActive("disconnectedSignalR")) toast.dismiss("disconnectedSignalR");

        // Reset connection and info to cause negotiation to happen again.
        setSrConnectionInfo(undefined);
        setSrConnection(undefined);

        // toast.error(
        //   "Connection with the server ended. Please log out and in again after completing any open contacts.",
        //   { autoClose: false, closeOnClick: false, closeButton: false }
        // );
      });
      localSrConnection.onreconnecting(() => {
        // if (!toast.isActive("disconnectedSignalR"))
        //   toast.error("Lost connection to the server. Reconnecting...", {
        //     toastId: "disconnectedSignalR",
        //     autoClose: false,
        //     closeOnClick: false,
        //     closeButton: false
        //   });
      });
      localSrConnection.onreconnected(() => {
        // toast.success("Reconnected to Server!");
        // if (toast.isActive("disconnectedSignalR")) toast.dismiss("disconnectedSignalR");
      });
      localSrConnection.start().catch(() => {
        setSrConnectionInfo(undefined);
        setSrConnection(undefined);
      });
      if (
        localSrConnection.state === HubConnectionState.Connected ||
        localSrConnection.state === HubConnectionState.Connecting
      ) {
        setSrConnection(conn => {
          if (conn) conn.stop();
          return localSrConnection;
        });
        // set connection topic behaviors
        setTopics(topics => {
          if (topics.length) {
            setPayloads(payloads => {
              topics.map(({ name }) => (payloads[name] = null));
              return { ...payloads };
            });
            topics.map(({ name }) =>
              localSrConnection.on(name, data => {
                setPayloads(oldPayloads => {
                  return { ...oldPayloads, [name]: data };
                });
              })
            );
          }
          return topics;
        });
      }
    }
  }, [srConnectionInfo, token, srConnection, setTopics, setPayloads, setSrConnection, setSrConnectionInfo]);

  // subscribe to signalR poll topic, respond by echoing the id
  const poll = userPoll as { id: string } | null;
  useEffect(() => subscribe(["userPoll"]), [subscribe]);
  useApi({
    method: "put",
    url: `Legacy/V0/SignalR/UserPoll/${poll?.id}`,
    valid: !!poll?.id,
    error: Error.PutUserPoll
  });

  return <></>;
};

export default SignalRConnection;
